import { memo, useCallback, useMemo } from 'react';
import { MainStackProps } from '../../../../types/screens';
import { StyleSheet, Text, View } from 'react-native';
import useGlobalStyles from '../../../../common/style_context';
import AppColors from '../../../../common/colors';
import TvPressable from '../../../../components/tv_pressable';
import { commonStyles } from '../../../../common/styles';
import localization from '../../../../common/localization';
import TextView from '../../../../components/text_view';
import DigitalKeyboard from '../../../../components/digital_keyboard';
import { useStateRef } from '../../../../common/hooks';
import { AccountProvider } from '../../../../data';
import { useTvToast } from '../../../../components/tv_toast/toast_provider';
import Utils from '../../../../utils/utils';
import KeyWrapper from '../../../../components/key_wrapper';

type Props = MainStackProps<'ParentPassDialogTv'>;

const dialogStyles = [
  `w=500,bg=${AppColors.containerGray},p=24,br=16`,
  'fs=32,fw=700,c=white,tc,mb=36',
  'mb=36',
  'h=470',
];

const ParentPassDialog = memo<Props>(({ navigation }) => {
  const toast = useTvToast();

  const [focused, focusedRef, setFocused] = useStateRef(0);

  const [oldPass, oldPassRef, setOldPass] = useStateRef('');
  const [newPass, newPassRef, setNewPass] = useStateRef('');
  const [confirmPass, confirmPassRef, setConfirmPass] = useStateRef('');

  const [container, titleStyle, inputContainer, keyboardContainer] =
    useGlobalStyles(dialogStyles);

  const onType = useCallback((n: number) => {
    const f =
      focusedRef.current === 0
        ? setOldPass
        : focusedRef.current === 1
        ? setNewPass
        : setConfirmPass;
    f(p => `${p}${n}`.slice(0, 12));
  }, []);
  const onRemove = useCallback(() => {
    const f =
      focusedRef.current === 0
        ? setOldPass
        : focusedRef.current === 1
        ? setNewPass
        : setConfirmPass;
    f(p => p.slice(0, -1));
  }, []);
  const onPrev = useCallback(() => setFocused(p => Math.max(0, p - 1)), []);
  const onNext = useCallback(() => setFocused(p => Math.min(2, p + 1)), []);

  const onSubmit = useCallback(() => {
    const oldPass = oldPassRef.current;
    const newPass = newPassRef.current;
    const confirmPass = confirmPassRef.current;

    const empty =
      oldPass.length === 0
        ? 0
        : newPass.length === 0
        ? 1
        : confirmPass.length === 0
        ? 2
        : undefined;
    if (empty !== undefined) {
      setFocused(empty);
      return;
    }

    if (newPass !== confirmPass) {
      toast?.show('danger', localization.settings.passNotSame);
      return;
    }

    AccountProvider.changeParentPass(oldPass, newPass).then(
      () => {
        toast?.show('success', localization.settings.parentPassSuccessChange);
        navigation.pop();
      },
      err => {
        toast?.show('danger', Utils.getTvErrorMessage(err));
      },
    );
  }, []);
  const cancel = useCallback(() => navigation.pop(), []);

  const onPress = useMemo(
    () => Array.from({ length: 3 }, (_, i) => () => setFocused(i)),
    [],
  );
  const submitText = useMemo(() => {
    if (
      oldPass.length === 0 ||
      newPass.length === 0 ||
      confirmPass.length === 0
    )
      return localization.general.next;
    return localization.general.enter;
  }, [oldPass.length === 0, newPass.length === 0, confirmPass.length === 0]);

  return (
    <View style={styles.container}>
      <TvPressable onPress={cancel} style={commonStyles.fill} />
      <KeyWrapper cancel={cancel} style={container}>
        <Text style={titleStyle}>
          {localization.settings.parentControlChangePass}
        </Text>
        <View style={inputContainer}>
          <TextView
            value={oldPass}
            focused={focused === 0}
            placeholder={localization.settings.oldPass}
            onPress={onPress[0]}
            secured
          />
        </View>
        <View style={inputContainer}>
          <TextView
            value={newPass}
            focused={focused === 1}
            placeholder={localization.settings.newPass}
            onPress={onPress[1]}
            secured
          />
        </View>
        <View style={inputContainer}>
          <TextView
            value={confirmPass}
            focused={focused === 2}
            placeholder={localization.settings.confirmPass}
            onPress={onPress[2]}
            secured
          />
        </View>
        <View style={keyboardContainer}>
          <DigitalKeyboard
            enabled
            submitText={submitText}
            onType={onType}
            onRemove={onRemove}
            onSubmit={onSubmit}
            onPrev={onPrev}
            onNext={onNext}
          />
        </View>
      </KeyWrapper>
    </View>
  );
});
ParentPassDialog.displayName = 'ParentPassDialogTv';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default ParentPassDialog;
